import React from 'react';

import { css } from '@emotion/react';
import { mqMin } from '@lucid/theme';

import { LucidTheme, Component } from '../../types/types';
import Box from '../box/box';
import Tile, { TileFields } from '../tile/tile';

export interface TileGridProps<T> {
  /** The list of tiles to be displayd in the `TileGrid` component. */
  tiles: T[];
  /** The page number that the tiles should start from. */
  page?: number;
  /** The page type related for the tiles. */
  pageType?: string;
  /** Limits the total number of tiles to be display at one time. `@default = 3`, `@max = 3`. */
  tilesPerPage?: number;
  /** The current page to display the tiles. */
  currentPage?: number;
}

export type TileGroupProps<T> = {
  /** Checks if the largeTile should be displayed on the right side. */
  isRight: boolean;
  /** The tiles that should be displayed as a large tile. */
  largeTiles: T[];
  /** The tiles that should be displayed as a medium tile. */
  tiles: T[];
};

const tileHalfStyles = css({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  height: '100%',
  width: '100%',
  [mqMin.tabletSmall]: {
    width: 'calc(50% - 20px)',
  },
});

const TileGrid = <T extends TileFields>({
  currentPage = 1,
  page = 1,
  pageType,
  tiles,
  tilesPerPage = 3,
}: TileGridProps<T>) => {
  if (tilesPerPage > 3 || tilesPerPage <= 0) throw new Error('tilesPerPage must be a number between 1 & 3.');

  const tileGroups = (currentTiles: T[]) =>
    currentTiles
      .map((_, i) => (i % tilesPerPage === 0 ? currentTiles.slice(i, i + tilesPerPage) : null))
      .filter(Boolean);

  const currentTileGroup = tileGroups(tiles)
    .slice(0, currentPage - page + 1)
    .reduce<TileGroupProps<T>[]>((previous, current, index) => {
      const largeTile = current!.find(currentTile => {
        if (currentTile.isSticky) return currentTile;
        return null;
      });

      const currentLargeTiles = largeTile
        ? {
            ...largeTile,
            size: 'large' as Component.Type['TileSize'],
          }
        : {
            ...current![0],
            isSticky: true,
            size: 'large' as Component.Type['TileSize'],
          };

      const otherTiles = current!.filter(currentTile => currentTile.nid !== currentLargeTiles.nid);

      const otherTilesSizes = otherTiles.map((currentTile, i) => {
        const size: Component.Type['TileSize'] =
          i === 0 ? (i === otherTiles.length - 1 ? 'large' : 'medium') : 'medium';

        return {
          ...currentTile,
          size,
        };
      });

      const isRight = index % 2 === 0 ? (current!.length === 1 ? false : true) : false;

      return [
        ...previous,
        {
          largeTiles: [currentLargeTiles],
          tiles: [...otherTilesSizes],
          isRight: isRight,
        },
      ];
    }, []);

  return (
    <>
      {currentTileGroup.map(({ largeTiles, tiles, isRight }, i: number) => (
        <Box
          key={`tileGroupContainer-${i}${pageType ? `-${pageType}` : ''}`}
          as="article"
          css={(theme: LucidTheme) => ({
            label: 'tileGroupContainer',
            maxWidth: '1200px',
            [mqMin.tabletSmall]: {
              display: 'flex',
              flexFlow: 'row nowrap',
              justifyContent: 'space-between',
              paddingTop: theme.space[6],
            },
            ['& > .tileHalf']: {
              '&:nth-of-type(1)': [
                {
                  order: isRight ? 1 : 'unset',
                },
                !tiles.length && {
                  width: '100%',
                },
              ],
            },
          })}
        >
          <Box
            key={`largeTiles-${i}`}
            className="tileHalf"
            css={[
              {
                label: 'largeTile',
              },
              tileHalfStyles,
            ]}
          >
            {largeTiles?.map((tile: T, index) => (
              <Tile key={`largeTile-${index}`} size={tile.size} tile={tile} />
            ))}
          </Box>
          {tiles && !!tiles.length && (
            <Box
              key={`tiles-${i}`}
              className="tileHalf"
              css={[
                {
                  label: 'otherTiles',
                },
                tileHalfStyles,
              ]}
            >
              {tiles?.map((tile: T, index) => (
                <Tile key={`tile-${index}`} size={tile.size} tile={tile} />
              ))}
            </Box>
          )}
        </Box>
      ))}
    </>
  );
};

export { TileGrid };
export default TileGrid;
